import React, { Children } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
export function PrivacyPolicy({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="min-h-screen bg-[#111111] text-gray-300" data-id="element-271">
      {/* Header Bar */}
      <div className="w-full bg-[#0a0a0a] border-b border-gray-800 sticky top-0 z-40" data-id="element-272">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-273">
          <button onClick={() => {
          onNavigate('home');
          window.scrollTo(0, 0);
        }} className="flex items-center gap-2 text-sm text-gray-400 hover:text-accent transition-colors duration-200" data-id="element-274">
            <ArrowLeft className="w-4 h-4" data-id="element-275" />
            Back to Home
          </button>
          <span className="text-sm font-bold text-gray-500 uppercase tracking-widest" data-id="element-276">
            A1C Shockproof
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16" data-id="element-277">
        <div className="flex items-center gap-3 mb-8" data-id="element-278">
          <Shield className="w-8 h-8 text-accent" data-id="element-279" />
          <h1 className="text-3xl md:text-4xl font-bold text-white" data-id="element-280">
            Privacy Policy
          </h1>
        </div>
        <p className="text-sm text-gray-500 mb-12" data-id="element-281">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })}
        </p>

        <div className="space-y-10 text-gray-400 leading-relaxed" data-id="element-282">
          <section className="space-y-4" data-id="element-283">
            <h2 className="text-xl font-bold text-white" data-id="element-284">1. Introduction</h2>
            <p data-id="element-285">
              A1C Shockproof ("we," "our," or "us") is committed to protecting
              the privacy of our customers and website visitors. This Privacy
              Policy explains how we collect, use, disclose, and safeguard your
              information when you visit our website and purchase our digital
              products.
            </p>
            <p data-id="element-286">
              By accessing our website or purchasing our products, you agree to
              the terms of this Privacy Policy. If you do not agree with the
              terms of this policy, please do not access our website.
            </p>
          </section>

          <section className="space-y-4" data-id="element-287">
            <h2 className="text-xl font-bold text-white" data-id="element-288">
              2. Information We Collect
            </h2>
            <h3 className="text-lg font-semibold text-gray-300" data-id="element-289">
              Personal Information
            </h3>
            <p data-id="element-290">
              When you make a purchase, we may collect the following personal
              information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-291">
              <li data-id="element-292">Full name</li>
              <li data-id="element-293">Email address</li>
              <li data-id="element-294">Billing address</li>
              <li data-id="element-295">
                Payment information (processed securely through Stripe; we do
                not store your full credit card details)
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-300 mt-6" data-id="element-296">
              Automatically Collected Information
            </h3>
            <p data-id="element-297">
              When you visit our website, we may automatically collect certain
              information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-298">
              <li data-id="element-299">IP address</li>
              <li data-id="element-300">Browser type and version</li>
              <li data-id="element-301">Operating system</li>
              <li data-id="element-302">Pages visited and time spent on pages</li>
              <li data-id="element-303">Referring website address</li>
              <li data-id="element-304">Device information</li>
            </ul>
          </section>

          <section className="space-y-4" data-id="element-305">
            <h2 className="text-xl font-bold text-white" data-id="element-306">
              3. How We Use Your Information
            </h2>
            <p data-id="element-307">We use the information we collect for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-308">
              <li data-id="element-309">To process and fulfill your digital product purchase</li>
              <li data-id="element-310">To send you your download link and purchase confirmation</li>
              <li data-id="element-311">
                To communicate with you about your order or customer support
                inquiries
              </li>
              <li data-id="element-312">
                To send occasional product updates or related educational
                content (only with your consent)
              </li>
              <li data-id="element-313">To improve our website and product offerings</li>
              <li data-id="element-314">To detect and prevent fraud</li>
              <li data-id="element-315">To comply with legal obligations</li>
            </ul>
          </section>

          <section className="space-y-4" data-id="element-316">
            <h2 className="text-xl font-bold text-white" data-id="element-317">
              4. Payment Processing
            </h2>
            <p data-id="element-318">
              All payment transactions are processed through{' '}
              <span className="text-white font-semibold" data-id="element-319">Stripe</span>, a
              PCI-DSS compliant payment processor. Your payment information is
              encrypted using 256-bit SSL technology and transmitted directly to
              Stripe's secure servers. We never store, process, or have access
              to your full credit card number.
            </p>
          </section>

          <section className="space-y-4" data-id="element-320">
            <h2 className="text-xl font-bold text-white" data-id="element-321">
              5. Cookies and Tracking Technologies
            </h2>
            <p data-id="element-322">
              We may use cookies, web beacons, and similar tracking technologies
              to collect information about your browsing activity on our
              website. These technologies help us analyze website traffic,
              customize content, and improve your experience.
            </p>
            <p data-id="element-323">
              You can control cookie preferences through your browser settings.
              Disabling cookies may affect certain features of our website.
            </p>
          </section>

          <section className="space-y-4" data-id="element-324">
            <h2 className="text-xl font-bold text-white" data-id="element-325">
              6. Third-Party Services
            </h2>
            <p data-id="element-326">
              We may share your information with the following third-party
              service providers:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-327">
              <li data-id="element-328">
                <span className="text-white" data-id="element-329">Stripe</span> — Payment processing
              </li>
              <li data-id="element-330">
                <span className="text-white" data-id="element-331">Email service provider</span> — For
                delivering your purchase and communications
              </li>
              <li data-id="element-332">
                <span className="text-white" data-id="element-333">Analytics tools</span> — For
                website performance analysis (anonymized data)
              </li>
            </ul>
            <p data-id="element-334">
              These third parties are contractually obligated to protect your
              information and may only use it for the specific purposes we have
              outlined.
            </p>
          </section>

          <section className="space-y-4" data-id="element-335">
            <h2 className="text-xl font-bold text-white" data-id="element-336">7. Data Retention</h2>
            <p data-id="element-337">
              We retain your personal information for as long as necessary to
              fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law. Purchase
              records are retained for accounting and tax compliance purposes.
            </p>
          </section>

          <section className="space-y-4" data-id="element-338">
            <h2 className="text-xl font-bold text-white" data-id="element-339">8. Your Rights</h2>
            <p data-id="element-340">
              Depending on your jurisdiction, you may have the following rights
              regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-341">
              <li data-id="element-342">
                <span className="text-white font-semibold" data-id="element-343">Access</span> —
                Request a copy of the personal data we hold about you
              </li>
              <li data-id="element-344">
                <span className="text-white font-semibold" data-id="element-345">Correction</span> —
                Request correction of inaccurate personal data
              </li>
              <li data-id="element-346">
                <span className="text-white font-semibold" data-id="element-347">Deletion</span> —
                Request deletion of your personal data
              </li>
              <li data-id="element-348">
                <span className="text-white font-semibold" data-id="element-349">Opt-out</span> —
                Unsubscribe from marketing communications at any time
              </li>
            </ul>
            <p data-id="element-350">
              To exercise any of these rights, please contact us at the email
              address provided below.
            </p>
          </section>

          <section className="space-y-4" data-id="element-351">
            <h2 className="text-xl font-bold text-white" data-id="element-352">
              9. Children's Privacy
            </h2>
            <p data-id="element-353">
              Our website and products are not intended for individuals under
              the age of 18. We do not knowingly collect personal information
              from children. If you believe we have inadvertently collected
              information from a minor, please contact us immediately.
            </p>
          </section>

          <section className="space-y-4" data-id="element-354">
            <h2 className="text-xl font-bold text-white" data-id="element-355">
              10. Changes to This Policy
            </h2>
            <p data-id="element-356">
              We reserve the right to update this Privacy Policy at any time.
              Changes will be posted on this page with an updated "Last updated"
              date. Your continued use of our website after any changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-4" data-id="element-357">
            <h2 className="text-xl font-bold text-white" data-id="element-358">11. Contact Us</h2>
            <p data-id="element-359">
              If you have any questions or concerns about this Privacy Policy,
              please contact us at:
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mt-4" data-id="element-360">
              <p className="text-white font-semibold" data-id="element-361">A1C Shockproof</p>
              <p className="text-gray-400 mt-2" data-id="element-362">
                Email: support@a1cshockproof.com
              </p>
            </div>
          </section>
        </div>

        {/* Back Button */}
        <div className="mt-16 pt-8 border-t border-gray-800" data-id="element-363">
          <button onClick={() => {
          onNavigate('home');
          window.scrollTo(0, 0);
        }} className="flex items-center gap-2 text-accent hover:text-[#d55515] transition-colors duration-200 font-semibold" data-id="element-364">
            <ArrowLeft className="w-5 h-5" data-id="element-365" />
            Back to Home
          </button>
        </div>
      </div>
    </div>;
}